// src/pages/picking/PickingHistoryList.tsx
import React, { useEffect, useState } from 'react';
import axios from '@/utils/axiosInstance';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { FaFileExport } from "react-icons/fa";
import { ChevronsLeft, ChevronsRight } from 'lucide-react';

interface PickingTask {
  id: string;
  createdAt: string;
  completedAt: string;
  qty: number;
  status: 'PENDING' | 'COMPLETED' | 'CANCELLED';
  product?: { name: string };
  sourceLocation?: { name: string };
  outboundOrderDetail?: {
    outboundOrder?: {
      outboundOrderNo: string;
    };
  };
}

const PickingHistoryList = () => {
  const [tasks, setTasks] = useState<PickingTask[]>([]);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [selectedTasks, setSelectedTasks] = useState<{ [taskId: string]: string }>({});

  const { logout } = useAuth();
  const navigate = useNavigate();
  const API = process.env.REACT_APP_API_URL;

  const getAuthHeader = () => ({
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token')}` || '',
      'Content-Type': 'application/json',
    },
  });

  const fetchTasks = async () => {
    try {
      const res = await axios.get(`${API}/picking-tasks`, {
        ...getAuthHeader(),
        params: {
          page: currentPage,
          limit: rowsPerPage,
          search,
          status: 'COMPLETED',
          sortOrder: 'DESC',
        },
      });
	  
      setTasks(res.data.data || []);
      setTotal(res.data.total || 0);
	  
    } catch (err: any) {
      if (err.response?.status === 401) {
        logout();
        navigate('/login');
      }
    }
  };

  const handleExport = () => {
    const exportData = tasks.map((task) => ({
      'Outbound Order No': task.outboundOrderDetail?.outboundOrder?.outboundOrderNo || '-',
      'Outbound Order Date': dayjs(task.createdAt).format('DD/MM/YYYY'),
      'Product': task.product?.name || '-',
      'Location': task.sourceLocation?.name || '-',
      'Qty': task.qty,
      'Completed Date': dayjs(task.completedAt).format('DD/MM/YYYY'),
    }));
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'PickingHistory');
    XLSX.writeFile(workbook, 'PickingHistory.xlsx');
  };

  useEffect(() => {
    fetchTasks();
  }, [currentPage, rowsPerPage]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setCurrentPage(1);
      fetchTasks();
    }, 300);
    return () => clearTimeout(timeout);
  }, [search]);

  const maxPage = Math.ceil(total / rowsPerPage);

  return (
    <div className="p-1 mx-auto max-w-6xl">
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Picking - History</h2>
        </div>

        {/* 🔍 Filter & Rows per Page */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-4 gap-2">
          <div className="relative w-full md:w-1/3">
            <input
              type="text"
              placeholder="Search..."
              className="border px-2 py-1 rounded text-[13px] w-full pr-8"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                &#10005;
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            <select
              value={rowsPerPage}
              onChange={(e) => setRowsPerPage(Number(e.target.value))}
              className="border rounded px-2 py-1 text-[13px]"
            >
              {[10, 25, 50, 100].map((num) => (
                <option key={num} value={num}>
                  {num} / page
                </option>
              ))}
            </select>
            |
            <button
              onClick={handleExport}
              className="text-blue-600 hover:text-blue-800 text-lg"
              title="Export to Excel"
            >
              <FaFileExport />
            </button>
          </div>
        </div>

        {/* 📄 Table */}
        <div className="overflow-x-auto border rounded mb-2">
          <table className="min-w-full text-[13px]">
            <thead className="bg-gray-100">
              <tr>
                <th className="border px-2 py-2 text-center">✔</th>
                <th className="border px-2 py-1 text-center">Outbound Order No</th>
                <th className="border px-2 py-1 text-center">Outbound Order Date</th>
                <th className="border px-2 py-1 text-center">Product</th>
                <th className="border px-2 py-1 text-center">Source Location</th>
                <th className="border px-2 py-1 text-center">Qty</th>
                <th className="border px-2 py-1 text-center">Completed Date</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((task) => (
                <tr key={task.id} className="hover:bg-gray-50">
                  <td className="border px-2 py-1 text-center">
                    <input
                      type="checkbox"
                      disabled={task.status !== 'COMPLETED'}
                      checked={task.id in selectedTasks}
                      onChange={(e) => {
                        const updated = { ...selectedTasks };
                        if (!e.target.checked) delete updated[task.id];
                        else updated[task.id] = 'v';
                        setSelectedTasks(updated);
                      }}
                    />
                  </td>
                  <td className="border px-2 py-1">{task.outboundOrderDetail?.outboundOrder?.outboundOrderNo || '-'}</td>
                  <td className="border px-2 py-1">{dayjs(task.createdAt).format('DD/MM/YYYY H:mm')}</td>
                  <td className="border px-2 py-1">{task.product?.name || '-'}</td>
                  <td className="border px-2 py-1">{task.sourceLocation?.name || '-'}</td>
                  <td className="border px-2 py-1 text-right">{task.qty}</td>
                  <td className="border px-2 py-1">{task.completedAt ? dayjs(task.completedAt).format('DD/MM/YYYY H:mm') : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex justify-between items-center mt-4 text-[13px] flex-wrap gap-2">
          <div>
            Showing {(currentPage - 1) * rowsPerPage + 1}–{Math.min(currentPage * rowsPerPage, total)} of {total}
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            <button
              className="px-2 py-1 border rounded disabled:opacity-30"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(1)}
              title="First Page"
            >
              <ChevronsLeft size={18} />
            </button>

            <button
              className="px-2 py-1 border rounded disabled:opacity-30"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            >
              «
            </button>

            {currentPage > 3 && (
              <>
                <button className="px-2 py-1 border rounded" onClick={() => setCurrentPage(1)}>1</button>
                {currentPage > 4 && <span className="px-1">...</span>}
              </>
            )}

            {Array.from({ length: maxPage }, (_, i) => i + 1)
              .filter((page) => Math.abs(page - currentPage) <= 2)
              .map((page) => (
                <button
                  key={page}
                  className={`px-2 py-1 border rounded ${
                    page === currentPage ? 'bg-blue-600 text-white' : 'hover:bg-gray-100'
                  }`}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              ))}

            {currentPage < maxPage - 2 && (
              <>
                {currentPage < maxPage - 3 && <span className="px-1">...</span>}
                <button className="px-2 py-1 border rounded" onClick={() => setCurrentPage(maxPage)}>{maxPage}</button>
              </>
            )}

            <button
              className="px-2 py-1 border rounded disabled:opacity-30"
              disabled={currentPage === maxPage}
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, maxPage))}
            >
              »
            </button>

            <button
              className="px-2 py-1 border rounded disabled:opacity-30"
              disabled={currentPage === maxPage}
              onClick={() => setCurrentPage(maxPage)}
              title="Last Page"
            >
              <ChevronsRight size={18} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PickingHistoryList;
